/**
 * Hud.ts — HUD superior: cor + nome + relogio + contadores de planetas/naves/constelacao
 * + o percentual do envio rapido; e a barra inferior de toque (chips 25..100%, Todos, Varios).
 */

import { corHex } from '../config';
import type { EstadoJogo } from '../domain/tipos';

export class Hud {
  private readonly elCor: HTMLElement;
  private readonly elNome: HTMLElement;
  private readonly elTempo: HTMLElement;
  private readonly elPlanetasTotal: HTMLElement;
  private readonly elPlanetasMeus: HTMLElement;
  private readonly elNavesMinhas: HTMLElement;
  private readonly elNavesMortas: HTMLElement;
  private readonly elHudConstelacao: HTMLElement;
  private readonly elConstelacaoValor: HTMLElement;
  private readonly elEnvio: HTMLElement;
  private readonly elBtnMulti: HTMLElement | null;

  constructor(private readonly estado: EstadoJogo) {
    this.elCor = obrigatorio('swCor');
    this.elNome = obrigatorio('swNomeJogador');
    this.elTempo = obrigatorio('swTempo');
    this.elPlanetasTotal = obrigatorio('swPlanetasTotal');
    this.elPlanetasMeus = obrigatorio('swPlanetasMeus');
    this.elNavesMinhas = obrigatorio('swNavesMinhas');
    this.elNavesMortas = obrigatorio('swNavesMortas');
    this.elHudConstelacao = obrigatorio('swHudConstelacao');
    this.elConstelacaoValor = obrigatorio('swConstelacaoValor');
    this.elEnvio = obrigatorio('swEnvio');
    this.elBtnMulti = document.getElementById('swBtnMulti');
  }

  setNome(nome: string): void {
    this.elNome.textContent = nome;
  }

  /** Chamar no partida_inicio. Atualiza cor + contadores totais. */
  atualizarTudo(): void {
    const hex = corHex(this.estado.suaCor);
    this.elCor.style.background = hex;
    this.elCor.style.color = hex;
    document.documentElement.style.setProperty('--sw-cor-jogador', hex);

    let meus = 0;
    for (const p of this.estado.planetas.values()) {
      if (p.donoId === this.estado.suaCor) meus++;
    }
    this.elPlanetasTotal.textContent = String(this.estado.planetas.size);
    this.elPlanetasMeus.textContent = String(meus);
    this.atualizarPct();
    this.atualizarRelogio();
  }

  /** Chamar periodicamente (a cada 10 ticks). */
  atualizarContadoresNaves(): void {
    let minhas = 0;
    for (const n of this.estado.naves.values()) {
      if (n.donoId === this.estado.suaCor) minhas++;
    }
    this.elNavesMinhas.textContent = String(minhas);
    this.elNavesMortas.textContent = String(this.estado.navesMortas);

    let meus = 0;
    for (const p of this.estado.planetas.values()) {
      if (p.donoId === this.estado.suaCor) meus++;
    }
    this.elPlanetasMeus.textContent = String(meus);

    this.atualizarConstelacao();
  }

  /** Relogio regressivo: tempo_limite_partida - decorrido (laranja no ultimo minuto). */
  atualizarRelogio(): void {
    const limite = Number(this.estado.config?.tempo_limite_partida ?? 480);
    const resta = Math.max(0, limite - this.estado.tempoDecorrido);
    const m = Math.floor(resta / 60);
    const s = resta % 60;
    this.elTempo.textContent = `${m}:${s < 10 ? '0' + s : s}`;
    this.elTempo.classList.toggle('urgente', resta <= 60);
  }

  /** Percentual do envio rapido no card e nos chips da barra. */
  atualizarPct(): void {
    const pct = this.estado.pctEnvio;
    this.elEnvio.textContent = `${Math.round(pct * 100)}%`;
    document.querySelectorAll<HTMLElement>('.sw-chip[data-pct]').forEach((chip) => {
      chip.classList.toggle('ativo', Math.abs(parseFloat(chip.dataset.pct || '0') - pct) < 0.001);
    });
    if (this.elBtnMulti) this.elBtnMulti.classList.toggle('ativo', this.estado.modoMulti);
  }

  private atualizarConstelacao(): void {
    if (!this.estado.suaCor) return;
    let total = 0;
    let dominados = 0;
    for (const p of this.estado.planetas.values()) {
      if (p.constelacao !== this.estado.suaCor) continue;
      total++;
      if (p.donoId === this.estado.suaCor) dominados++;
    }
    if (total === 0) {
      this.elHudConstelacao.style.display = 'none';
      return;
    }
    this.elHudConstelacao.style.display = '';
    if (dominados === total) {
      this.elConstelacaoValor.innerHTML = '<span style="color:#fbbf24">&#10022; +20% producao</span>';
    } else {
      this.elConstelacaoValor.textContent = `${dominados} / ${total} planetas`;
    }
  }
}

function obrigatorio(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Elemento #${id} nao encontrado no DOM`);
  return el;
}
